import React from 'react';
import { gql } from '@ts-gql/tag';
import { useMutation } from '@ts-gql/apollo';

const mutation = gql`
  mutation DeleteJob($cloudId: String!) {
    deleteJob(input: { cloudId: $cloudId }) {
      correlationId
      validationMessage {
        field
        message
      }
      messagesSuccessfullyProcessed
    }
  }
` as import('../../__generated__/ts-gql/DeleteJob').type;

export const useDeleteJob = () => {
  const tuple = useMutation(mutation);

  const [_mutate, result] = tuple;

  const mutate = React.useCallback<typeof _mutate>(
    (options) => {
      if (!options.variables.cloudId) {
        throw new Error('variable "cloudId" is required in useDeleteJob');
      }
      return _mutate({
        context: { service: 'core' },
        update(cache) {
          const { cloudId } = options.variables;
          const id = cache.identify({ __typename: 'Job', cloudId });
          cache.evict({ id });
          // in theory, the above should be all we need. But the job is not going from the search results, so go the bigger hammer
          cache.evict({ fieldName: 'searchJobs' });
          cache.evict({ fieldName: 'searchTasks' });
          cache.evict({ fieldName: 'jobGetRecurrences' });
          // this might seem heavy, delete one job so blow away EVERY recurrence historylist
          // but, in practice, the refetchQueries would refetch even if we hadn't executed it to start off with
          // and jobRecurrenceHistoryList is a bit slow.
          // What this means is if they go look at the history of a different job, delete any job,
          // then look at the history of that first job again, it'll hit the network for it. Not a huge concern
          cache.evict({ fieldName: 'jobRecurrenceHistoryList' });
        },
        refetchQueries: ['GetNotificationCountForUser'],
        ...options,
      });
    },
    [_mutate]
  );

  return React.useMemo<typeof tuple>(() => [mutate, result], [mutate, result]);
};
